import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { reduxForm } from 'redux-form';
import { connect } from 'react-redux';

import { Field } from 'redux-form';
import InputGroup from '../InputGroup';
import TextareaGroup from '../TextareaGroup';

import validate from './validations';

import Button from '../../components/Button';
import TwoGroup from '../../components/TwoGroup';

import GetUpdatesCheckbox from '../Widget/GetUpdatesCheckbox';
import LegalInfo from '../Widget/LegalInfo';

import { LoadingSpinner } from '../../components/LoadingSpinner';

import { capitalization, copyFor } from '../../copy/copy';

import { getCachedFullName } from '../../utils/formCaching/helpers';

const namePlaceholder = () => copyFor('placeholders', 'yourName', 'email');
const escapedNamePlaceholder = () =>
    namePlaceholder().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const DetailsFormContainer = styled.form`
    flex: 1;
    padding: 0;
    display: flex;
    flex-direction: column;
`;

const Row = styled.div`
    margin: 0 -0.5em 12px -0.5em;
    transition: opacity 0.6s;

    .label-response {
        @media (min-width: 30em) {
            position: absolute;
            width: 100%;
            bottom: 142px;
            transition: bottom 0.6s;
        }
    }

    &.focused {
        opacity: 0;

        &.row-wrapper-3 {
            opacity: 1;

            .label-response {
                bottom: calc(100% - 16px);
            }

            textarea {
                height: 200px;
            }
        }
    }
`;

const FullRow = styled.div`
    width: 100%;
    padding: 0 8px;
`;

const HalfRow = styled.div`
    width: 50%;
    padding: 0 0.5em;
    float: left;
`;

const ButtonGroup = styled.div`
    flex: 1;
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    margin-top: 0.75em;

    > div {
        position: relative;
    }

    button {
        &.submitButton {
            ${props => (props.isSubmitting ? `text-indent: -99999px;` : '')};
            margin-left: 0.5em;

            @media (max-width: 30em) {
                width: calc(50% - 0.5em);
            }
        }

        &.middleButton {
            @media (max-width: 30em) {
                width: calc(50% - 0.5em);
                margin-right: 0.5em;
            }
        }

        &:focus {
            outline: none;
        }

        + div {
            position: absolute;
            display: none;
            top: 7px;
            right: 24%;
            margin-right: -16px;
            width: 32px !important;
            height: 32px !important;
            ${props => (props.isSubmitting ? `display: block` : '')};
        }
    }
`;

const LegalInfoWrapper = styled.div`
    position: absolute;
    right: calc(100% + 1.2em);
    bottom: 10px;
    width: 45%;

    @media (max-width: 30em) {
        right: 0;
        width: 100%;
        bottom: 70px;
    }
`;

const FormGroupWrapper = styled.div`
    @media (max-width: 30em) {
        flex: 1;
        display: flex;
        flex-direction: row;
        margin: 0 -0.5em;

        > div {
            flex: 1;
            display: flex;
            flex-direction: column;
            margin: 0 0.5em;
        }
    }
`;

const replacePlaceholders = (value, previousValue, allValues) => {
    let newValue =
        allValues.fullName != null
            ? value.replace(/@userFullName/g, allValues.fullName)
            : value;
    return newValue;
};

const DetailsForm = props => (
    <DetailsFormContainer onSubmit={props.handleSubmit} autocomplete="on">
        <Row className="row-wrapper-1">
            <HalfRow>
                <Field
                    name="prefix"
                    component={InputGroup}
                    type="text"
                    label={copyFor('formFields', 'prefix', 'email', capitalization.all)}
                    placeholder={copyFor('placeholders', 'prefix', 'email')}
                />
            </HalfRow>
            <HalfRow>
                <Field
                    name="fullName"
                    component={InputGroup}
                    type="text"
                    label={copyFor('formFields', 'fullName', 'email', capitalization.all)}
                    placeholder={copyFor('placeholders', 'fullName', 'email')}
                    onBlur={(event, value) =>
                        value != null &&
                        value !== '' &&
                        !props.isEmailMessageChanged &&
                        props.change(
                            'message',
                            props.initialValues.message
                                .replace(RegExp(escapedNamePlaceholder(), 'g'), value)
                                .replace(/@localRepresentative/g, props.localRepFullName)
                        )
                    }
                />
            </HalfRow>
        </Row>
        <Row className="row-wrapper-2">
            <FullRow>
                <Field
                    name="email"
                    component={InputGroup}
                    type="email"
                    label={copyFor(
                        'formFields',
                        'emailAddress',
                        'email',
                        capitalization.all
                    )}
                    placeholder={copyFor('placeholders', 'emailAddress', 'email')}
                />
            </FullRow>
        </Row>
        <Row className="row-wrapper-3">
            <FullRow>
                <Field
                    title="message"
                    name="message"
                    component={TextareaGroup}
                    label={copyFor('formFields', 'message', 'email', capitalization.all)}
                    placeholder={copyFor('placeholders', 'message', 'email')}
                    class="message-response"
                    classLabel="label-response"
                    onChange={props.onEmailMessageChange}
                    normalize={replacePlaceholders}
                />
            </FullRow>
        </Row>
        <LegalInfoWrapper>
            <FormGroupWrapper>
                <GetUpdatesCheckbox />
                <LegalInfo align="mobile-right" />
            </FormGroupWrapper>
        </LegalInfoWrapper>
        <ButtonGroup isSubmitting={props.isSubmitting}>
            <TwoGroup>
                <Button
                    label={copyFor('buttons', 'back', 'email', capitalization.eachWord)}
                    onClick={props.onBackFromRepAction}
                    className="middleButton"
                />
                <Button
                    primary={true}
                    label={copyFor('buttons', 'send', 'email', capitalization.eachWord)}
                    type="submit"
                    className="submitButton"
                    disabled={props.invalid}
                    isSubmitting={props.isSubmitting}
                />
                <LoadingSpinner />
            </TwoGroup>
        </ButtonGroup>
    </DetailsFormContainer>
);

DetailsForm.propTypes = {
    change: PropTypes.func.isRequired,
    initialValues: PropTypes.object.isRequired,
    localRepFullName: PropTypes.string,
    handleSubmit: PropTypes.func.isRequired,
    onEmailMessageChange: PropTypes.func.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    invalid: PropTypes.bool.isRequired,
    isEmailMessageChanged: PropTypes.bool,
    onBackFromRepAction: PropTypes.func
};

export default compose(
    connect(state => ({
        initialValues: {
            getUpdates: true,
            message: state.widget.activeAction.text
                .replace(
                    /@localRepresentative/g,
                    state.localRep.localRepData.representative !== undefined
                        ? state.localRep.localRepData.representative[0].fullName
                        : ''
                )
                .replace(/@userFullName/g, namePlaceholder()),
            prefix: state.cachedFormData.prefix || '',
            fullName: getCachedFullName(state),
            email: state.cachedFormData.email || ''
        },
        localRepFullName:
            state.localRep.localRepData.representative !== undefined
                ? state.localRep.localRepData.representative[0].fullName
                : ''
    })),
    reduxForm({
        form: 'emailDetailsForm',
        validate
    })
)(DetailsForm);



// WEBPACK FOOTER //
// ./src/components/Email/DetailsForm.jsx